import { ApiUnavailableState, LoadingState, NotConfiguredState } from "../components/states";
import { AdminCard, Pill, RailKv } from "../components/dashboard";
import { usePlatformStatus } from "../hooks/usePlatformStatus";
import { useAppContext } from "../hooks/useAppContext";

/**
 * Bot Configuration — the ODIN bot's identity, capabilities and safety posture.
 * Layout uses the ODIN design system; identity/commands are honest "—" until a
 * server is connected, while the Safety card renders real platform + safety
 * data. No fabricated bot facts.
 */

export function BotConfig(): JSX.Element {
  const status = usePlatformStatus();
  const { guildName } = useAppContext();
  const ready = status.kind === "ready" ? status.data : null;
  const connected = Boolean(guildName);
  const dash = <span style={{ color: "var(--text-tertiary)" }}>—</span>;

  return (
    <div className="ax-content" style={{ gridTemplateColumns: "1fr" }}>
      <div className="ax-workspace">
        <div className="ax-head">
          <div>
            <h1 className="ax-head__title">Bot Configuration</h1>
            <p className="ax-head__sub">The ODIN bot&apos;s identity, capabilities and safety posture.</p>
          </div>
        </div>

        {status.kind === "loading" ? <LoadingState label="Contacting the control API…" /> : null}
        {status.kind === "not-configured" ? (
          <NotConfiguredState title="Control API URL not configured">
            Set <code>PUBLIC_API_URL</code> when building the dashboard to see live status here.
          </NotConfiguredState>
        ) : null}
        {status.kind === "unavailable" ? <ApiUnavailableState detail={status.detail} /> : null}

        {ready ? (
          <div className="ax-sections">
            <AdminCard title="Identity">
              <div className="ax-kv-list">
                <RailKv k="Name" v={dash} />
                <RailKv k="Avatar" v={dash} />
                <RailKv k="Status" v={<Pill tone="neutral" label={connected ? "Connected" : "Not connected"} />} />
              </div>
            </AdminCard>

            <AdminCard title="Intents & commands">
              <div className="ax-kv-list">
                <RailKv k="Slash commands" v={dash} />
                <RailKv k="Message content" v={<Pill tone="neutral" label="—" />} />
              </div>
            </AdminCard>

            <AdminCard title="Safety">
              <div className="ax-kv-list">
                <RailKv k="Environment" v={ready.environment} />
                <RailKv
                  k="Live deployment"
                  v={<Pill tone={ready.liveDeploymentLocked ? "success" : "warning"} label={ready.liveDeploymentLocked ? "Locked (safe)" : "Enabled"} />}
                />
                <RailKv
                  k="Dry run"
                  v={<Pill tone={ready.safety.dryRun ? "success" : "warning"} label={ready.safety.dryRun ? "On" : "Off"} />}
                />
                <RailKv
                  k="Require approval"
                  v={<Pill tone={ready.safety.requireApproval ? "success" : "error"} label={ready.safety.requireApproval ? "On" : "Off"} />}
                />
              </div>
            </AdminCard>
          </div>
        ) : null}
      </div>
    </div>
  );
}
